import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, Check, ArrowRight, Crown, Shield, User as UserIcon } from 'lucide-react';
import { format } from 'date-fns';
import './InviteAccept.css';

/**
 * /invite/:token — the recipient's landing page after clicking an invite link.
 *
 *   • Fetches the invite preview (unauthed endpoint).
 *   • If the invite is invalid/expired/revoked/used, shows a friendly error.
 *   • Otherwise renders the org + role + inviter context and an "Accept" CTA.
 *   • If the visitor is NOT logged in: button goes to /register?invite=:token.
 *   • If the visitor IS logged in: button POSTs to /api/invites/:token/accept,
 *     then switches the current org and redirects to projects.
 */

function RoleBadge({ role }) {
  const Icon = role === 'owner' ? Crown : role === 'admin' ? Shield : UserIcon;
  return (
    <span className={`invite-accept-role role-${role}`}>
      <Icon size={12} />
      {role}
    </span>
  );
}

function InviteAccept({ user }) {
  const { token } = useParams();
  const navigate = useNavigate();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptedOrg, setAcceptedOrg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`/invites/${token}`)
      .then(res => { if (!cancelled) setPreview(res.data); })
      .catch(e => {
        if (!cancelled) {
          setError(e.response?.data?.error || 'This invite link is not valid.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const isAuthed = !!user;

  const handleAcceptAuthed = async () => {
    setAccepting(true);
    try {
      const res = await axios.post(`/invites/${token}/accept`);
      setAcceptedOrg(res.data.membership.organization);
      // Switch to the newly-joined org and force-reload to flush cached state.
      localStorage.setItem('currentOrgId', res.data.membership.organization._id);
      setTimeout(() => { window.location.href = '/'; }, 1200);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to accept invite.');
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="invite-accept-page">
        <div className="invite-accept-card invite-accept-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invite-accept-page">
        <div className="invite-accept-card">
          <div className="invite-accept-error">
            <AlertCircle size={20} />
            <div>
              <div className="invite-accept-error-title">This invite isn't valid</div>
              <div className="invite-accept-error-body">{error}</div>
            </div>
          </div>
          <Link to="/login" className="btn btn-secondary btn-block">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  if (acceptedOrg) {
    return (
      <div className="invite-accept-page">
        <div className="invite-accept-card invite-accept-success">
          <div className="invite-accept-success-icon"><Check size={28} /></div>
          <h2>You've joined {acceptedOrg.name}</h2>
          <p>Taking you to the workspace…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-accept-page">
      <div className="invite-accept-card">

        <div className="eyebrow">◆ Invitation</div>

        <h1 className="invite-accept-org">{preview.organization.name}</h1>

        <p className="invite-accept-line">
          {preview.inviter
            ? <><strong>{preview.inviter.name}</strong> has invited you to join as a <RoleBadge role={preview.role} /></>
            : <>You've been invited to join as a <RoleBadge role={preview.role} /></>}
        </p>

        {preview.email && (
          <div className="invite-accept-bound">
            This invite is for <strong>{preview.email}</strong>.
          </div>
        )}

        <div className="invite-accept-expires">
          Expires {format(new Date(preview.expiresAt), 'MMMM d, yyyy')}
        </div>

        <div className="invite-accept-actions">
          {isAuthed ? (
            <>
              <div className="invite-accept-signedin">
                Signed in as <strong>{user.name}</strong>
              </div>
              <button
                className="btn btn-primary btn-block"
                onClick={handleAcceptAuthed}
                disabled={accepting}
              >
                {accepting ? 'Joining…' : <>Accept invitation <ArrowRight size={16} /></>}
              </button>
            </>
          ) : (
            <Link
              to={`/register?invite=${token}`}
              className="btn btn-primary btn-block"
            >
              Accept invitation <ArrowRight size={16} />
            </Link>
          )}

          {!isAuthed && (
            <Link to="/login" className="invite-accept-already">
              Already have an account? Sign in first
            </Link>
          )}
        </div>

      </div>
    </div>
  );
}

export default InviteAccept;
